import { AddOutlined, ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, ThemeProvider } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AccountTabs from 'components/v2/accountTabs';
import PageHeader from 'components/v2/common/pageHeader';
import PageBackground from 'components/v2/PageBackground';
import CreateSignatureForm from 'views/v2/signatures/components/createSignatureForm';
import SignatureList from 'views/v2/signatures/components/signatureList';

import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadSignatures } from 'redux/signatures/signatures.middleware';
import {
    selectCanCreateSignature,
    selectSignatures,
    selectSignaturesLoading,
} from 'redux/signatures/signatures.slice';
import type { AppDispatch } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import { getErrorMessage } from 'utils/v2/errorHelper';

type ViewMode = 'list' | 'create';

export default function SignatureManagement() {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const signatures = useSelector(selectSignatures);
    const isLoading = useSelector(selectSignaturesLoading);
    const canCreate = useSelector(selectCanCreateSignature);

    const [view, setView] = useState<ViewMode>('list');

    useEffect(() => {
        void dispatch(loadSignatures())
            .unwrap()
            .catch((error: unknown) => {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, 'Failed to load signatures'),
                        type: 'error',
                    })
                );
            });
    }, [dispatch]);

    const handleSignatureCreated = () => {
        setView('list');
    };

    const showInitialLoader = isLoading && signatures.length === 0;

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground maxWidth="sm">
                <AccountTabs />

                {view === 'list' ? (
                    <>
                        <PageHeader size="small" title="signatures">
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddOutlined />}
                                onClick={() => setView('create')}
                                disabled={!canCreate && !isLoading}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {translate('createSignature')}
                            </Button>
                        </PageHeader>

                        {!canCreate && !showInitialLoader ? (
                            <Alert severity="info" sx={{ borderRadius: 2, mb: 2.5 }}>
                                You have reached the maximum of 10 signatures. Delete an existing
                                signature to create a new one.
                            </Alert>
                        ) : null}

                        {showInitialLoader ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <SignatureList />
                        )}
                    </>
                ) : (
                    <>
                        <PageHeader size="small" title="createSignature">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowBack />}
                                onClick={() => setView('list')}
                            >
                                {translate('back')}
                            </Button>
                        </PageHeader>

                        {!canCreate ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                You have reached the maximum of 10 signatures. Delete an existing
                                signature to create a new one.
                            </Alert>
                        ) : (
                            <CreateSignatureForm
                                onSuccess={handleSignatureCreated}
                                onCancel={() => setView('list')}
                            />
                        )}
                    </>
                )}
            </PageBackground>
        </ThemeProvider>
    );
}
